import type { SVGProps } from 'react';

import { cn } from '../lib/cn';

export interface TaleLogoProps extends SVGProps<SVGSVGElement> {
  /** Render as wordmark instead of icon-only. Default true. */
  wordmark?: boolean;
}

/**
 * Tale brand logo (wordmark + mark). See design/docs/branding.md and design/sources/shared/.
 * Total bbox is 73.65 × 20 design units; each glyph is rendered at its design coordinates.
 * `currentColor` controls the foreground; pass a `className` to retint via Tailwind tokens.
 */
export function TaleLogo({
  wordmark = true,
  className,
  ...props
}: TaleLogoProps) {
  if (!wordmark) {
    // Flattened 20×20 mark — same path as services/platform/public/assets/logo-*.svg
    // and TaleLogoIcon. Do not reuse the wordmark's design-space transforms here;
    // scaling those into a 20×20 viewBox clips into a solid blob.
    return (
      <svg
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Tale"
        role="img"
        className={cn('h-5 w-5', className)}
        {...props}
      >
        <path
          fill="currentColor"
          d="M5.53 14.987A5 5 0 0 0 7.1 18.63l.008.007a5.01 5.01 0 0 0 6.293.47l4.934-3.81A4.29 4.29 0 0 0 20 11.902v-4.6l-7.594 5.865a3.988 3.988 0 0 1-5.85-1.225 5 5 0 0 0-1.027 3.044m8.943-9.976a4.98 4.98 0 0 0-1.57-3.643h-.008A5.01 5.01 0 0 0 6.603.89L1.666 4.7A4.29 4.29 0 0 0-.001 8.095v4.6L7.596 6.83a3.987 3.987 0 0 1 5.85 1.225 5 5 0 0 0 1.026-3.044"
        />
      </svg>
    );
  }

  // The full wordmark composes icon (left+right arrows) + letters t,a,l,e
  // Each subpath uses design-local coordinates; we wrap them in a g with the
  // design-space transform to position them inside the 0..73.65 × 0..20 viewport.
  return (
    <svg
      viewBox="0 0 73.65 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Tale"
      role="img"
      className={cn('h-5 w-auto', className)}
      {...props}
    >
      {/* Icon-Right (under the left half) */}
      <g transform="translate(0,0.004) scale(0.13784)">
        <path
          d="M104.18993 36.06889c0.02201-8.6376-3.074-16.9928-8.7199-23.53-0.82-0.9267-1.68-1.8267-2.58-2.7l-0.06 0c-6.0134-5.67444-13.7932-9.11144-22.0376-9.73582-8.2444-0.62438-16.4531 1.60173-23.2524 6.30582l-35.54 27.43c-3.73424 2.8842-6.7576 6.586-8.83785 10.8211-2.08024 4.235-3.16201 8.8905-3.16218 13.6089l0 33.11 54.69003-42.22c3.2611-2.4326 7.0021-4.1441 10.975-5.0209 3.9729-0.8769 8.0868-0.899 12.0689-0.0649 3.9821 0.834 7.7413 2.5052 11.0284 4.9026 3.2871 2.3974 6.0269 5.4664 8.0377 9.0032 4.8099-6.2899 7.4079-13.992 7.3899-21.91z"
          fill="currentColor"
        />
      </g>
      {/* Icon-Left */}
      <g transform="translate(5.4892,7.3055) scale(0.13783)">
        <path
          d="M0.00009 55.31c-0.0166 8.637 3.0791 16.99 8.72 23.53 0.82 0.933 1.68 1.833 2.58 2.7l0.06 0.05c6.021 5.668 13.8056 9.096 22.0515 9.711 8.246 0.615 16.45311-1.62 23.2485-6.33099l35.51999-27.43001c3.734-2.884 6.758-6.586 8.838-10.8211 2.08-4.235 3.162-8.8905 3.162-13.6089l0-33.11-54.66999 42.22c-3.2619 2.4328-7.0038 4.1444-10.9774 5.0213-3.9736 0.8767-8.0883 0.8987-12.0712 0.06501-3.9828-0.834-7.74291-2.5051-11.0308-4.90261-3.288-2.3975-6.0288-5.4666-8.0406-9.0037-4.8096 6.2899-7.4074 13.992-7.39 21.91z"
          fill="currentColor"
        />
      </g>
      {/* Letter-t — design glyph height 144 mapped to 20 */}
      <g transform="translate(24.8296,0) scale(0.13889)">
        <path
          d="M0 51l0-20.18 19.12001 0 0-30.82 23.7 0 0 30.82 28.27 0 0 20.18-28.27 0 0 55.13c0 5.293 1.36699 9.363 4.09999 12.21 2.733 2.847 6.4 4.277 11 4.29 4.53 0.096 9.02502-0.802 13.17001-2.63l1.59999 21.42c-5.633 1.794-11.51899 2.66501-17.42999 2.58-10.913 0-19.663-3.047-26.25-9.14-6.587-6.093-9.883-15.163-9.89-27.21l0-56.65-19.12001 0z"
          fill="currentColor"
        />
      </g>
      {/* Letter-a */}
      <g transform="translate(37.2569,4.2736) scale(0.13889)">
        <path
          d="M0.0095 80.99c0-10.74 4.16299-19.10001 12.48999-25.08 8.327-5.98 20.993-9.8533 38-11.62l20.69-2.16 0-1.59c0-6.7333-2.16299-12.03-6.48999-15.89-4.327-3.86-10.10001-5.79-17.32001-5.79-6.667 0-12.17299 1.8467-16.51998 5.54-4.347 3.6933-6.88001 8.81-7.60001 15.35l-21.35999-4c1.533-10.2988 7.02199-19.5939 15.29999-25.91 8.427-6.56 18.48699-9.84 30.17999-9.84 14.173 0 25.56301 3.8267 34.17002 11.48 8.607 7.6533 12.92001 18.2367 12.94 31.75l0 70-23.27002 0 0-18.28c-3.339 5.694-8.24799 10.304-14.13998 13.28-6.245 3.331-13.22302 5.05-20.30002 5-10.84 0-19.67698-3.03-26.50998-9.09-3.318-2.858-5.959-6.41801-7.733-10.42101-1.775-4.004-2.63801-8.35099-2.52701-12.72898z m23.28-0.63c-0.058 2.05 0.36399 4.085 1.23199 5.942 0.868 1.858 2.15901 3.48699 3.76801 4.758 3.333 2.74 7.76301 4.10701 13.29001 4.1 9.06 0 16.23299-2.823 21.51998-8.47 5.287-5.647 7.997-13.06001 8.13001-22.24l0-4.58-20.92999 2.36c-8.86 0.92-15.577 2.9633-20.15 6.13-4.573 3.167-6.86002 7.167-6.86001 12z"
          fill="currentColor"
        />
      </g>
      {/* Letter-l */}
      <g transform="translate(52.9263,0) scale(0.13889)">
        <path
          d="M0 144l0-144 23.70001 0 0 144-23.70001 0z"
          fill="currentColor"
        />
      </g>
      {/* Letter-e */}
      <g transform="translate(58.8372,4.2812) scale(0.13889)">
        <path
          d="M0.00074 56.63509c-0.035-7.2868 1.169-14.5266 3.56-21.41 2.261-6.5568 5.68201-12.65371 10.10001-18 4.593-5.4255 10.37599-9.717 16.89999-12.54 7.243-3.1893 15.087-4.7853 23-4.68 7.958-0.1043 15.855 1.3955 23.22 4.41 6.478 2.6122 12.28697 6.6446 16.99997 11.8 4.382 4.8971 7.80804 10.5716 10.10003 16.73 2.34301 6.2543 3.53202 12.8814 3.51001 19.56 0.03 3.2653-0.11399 6.5299-0.42999 9.78l-83.68002 0c1.06 9.42 4.44299 16.89299 10.14999 22.42 5.707 5.527 12.91299 8.29 21.62 8.29 7.233 0 13.257-1.63 18.07-4.89 4.791-3.219 8.14401-8.175 9.35001-13.82l22.42996 5.64c-2.58899 10.11-8.89096 18.875-17.64996 24.55-8.847 5.8-19.71999 8.7-32.62 8.7-7.967 0.095-15.86401-1.5-23.17001-4.68-6.63-2.827-12.536-7.114-17.27999-12.54-4.547-5.319-8.094-11.418-10.47001-18-2.489-6.831-3.74499-14.0498-3.70999-21.32z m24.34-12.46l57.91998 0c-0.78-7.6533-3.64999-13.8-8.60999-18.44-4.96-4.64-11.65299-6.9733-20.07998-7-7.507 0-13.81302 2.1967-18.92002 6.59-5.107 4.3933-8.543 10.6767-10.30999 18.85z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}
